import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';

const TrustAdminLogin = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [email, setEmail] = useState('iicontact@cloudai.in');
  const [loginError, setLoginError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  // New state to support admin comments (no access condition — page is admin-only)
  const [newComments, setNewComments] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Helper to format possible timestamp shapes safely
  const formatDateTime = (time) => {
    try {
      if (!time) return '-';
      const ts = (time && time.$date) ? time.$date : time;
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch (e) {
      return '-';
    }
  };

  // Helper to extract numeric timestamp for sorting (returns 0 when invalid)
  const getTimestamp = (time) => {
    try {
      if (!time) return 0;
      const ts = (time && time.$date) ? time.$date : time;
      const d = new Date(ts);
      if (isNaN(d.getTime())) return 0;
      return d.getTime();
    } catch (e) {
      return 0;
    }
  };

  const handleGoogleLogin = () => {
    if (email === 'iicontact@cloudai.in') {
      setIsLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Invalid email. Please use valid credentials.');
    }
  };

  const handleDelete = async (jobRunId) => {
    try {
      await apiClient.delete(`/api/v2/jobs/run/${jobRunId}`);
      setJobs(jobs.filter(job => job.jobRunId !== jobRunId));
    } catch (err) {
      const errorMessage = err.response ? `${err.response.status}: ${err.response.data.message}` : err.message;
      setError(`Failed to delete job. Server says: ${errorMessage}`);
      console.error(err);
    }
  };

  const handleJobClick = async (jobRunId) => {
    setIsDetailsLoading(true);
    try {
      const response = await apiClient.get(`/api/v2/jobs/run/${jobRunId}`);
      setSelectedJob(response.data);
    } catch (err) {
      setError('Failed to fetch job details.');
      console.error(err);
    }
    setIsDetailsLoading(false);
  };

  // Helpers for admin comments
  const handleCommentChange = (taskName, text) => {
    setNewComments(prev => ({ ...prev, [taskName]: text }));
  };

  const handleAddComment = async (e, taskName) => {
    e.preventDefault();
    setSubmitError(null);
    const commentText = (newComments[taskName] || '').trim();
    if (!commentText) return;

    try {
      const response = await apiClient.post(
        `/api/v2/jobs/run/${selectedJob.jobRunId}/tasks/${taskName}/comments`,
        { text: commentText, userId: 'Admin' }
      );

      // API should return the created comment. If not, build a fallback comment object.
      const created = response && response.data ? response.data : { id: Date.now(), text: commentText, userId: 'Admin', timestamp: new Date().toISOString() };

      setSelectedJob(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => (t.taskName === taskName ? { ...t, comments: [created, ...(t.comments || [])] } : t)),
      }));

      setNewComments(prev => ({ ...prev, [taskName]: '' }));
    } catch (err) {
      console.error('Failed to add admin comment', err);
      setSubmitError('Failed to submit comment. Please try again.');
    }
  };

  const handleBackToList = () => {
    setSelectedJob(null);
    setError(null);
  };

  const renderCommentText = (text) => {
    if (!text) return '';
    const segments = String(text).split(/(https?:\/\/[^\s]+|www\.[^\s]+)/gi);
    return segments.map((segment, index) => {
      if (!segment) return null;
      const isUrl = /^https?:\/\//i.test(segment) || /^www\./i.test(segment);
      if (!isUrl) {
        return <React.Fragment key={`${segment}-${index}`}>{segment}</React.Fragment>;
      }
      const href = /^www\./i.test(segment) ? `https://${segment}` : segment;
      return (
        <a
          key={`${segment}-${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {segment}
        </a>
      );
    });
  };

  useEffect(() => {
    if (isLoggedIn) {
      const fetchJobs = async () => {
        setIsLoading(true);
        try {
          const response = await apiClient.get('/api/v2/jobs');
          // Sort jobs by startTime (created date) descending so newest appear first
          const sorted = Array.isArray(response.data)
            ? response.data.slice().sort((a, b) => getTimestamp(b.startTime) - getTimestamp(a.startTime))
            : response.data;
          setJobs(sorted);
        } catch (err) {
          setError('Failed to fetch jobs.');
          console.error(err);
        }
        setIsLoading(false);
      };

      fetchJobs();
    }
  }, [isLoggedIn]);

  // Compact table styles for a tighter, more condensed list view
  const compact = {
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.9rem', minWidth: '720px' },
    th: { padding: '13px 16px', textAlign: 'left', fontSize: '0.75rem', color: '#475569', background: '#f8fafc', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '15px 16px', verticalAlign: 'middle', color: '#334155', borderBottom: '1px solid #e2e8f0' },
    deleteBtn: { padding: '7px 11px', fontSize: '0.75rem', cursor: 'pointer', backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '7px', fontWeight: '700' },
    idLink: { color: '#2563eb', fontWeight: '700', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9rem' }
  };

  // Get current user email from localStorage to authorize admin access
  const userStr = localStorage.getItem("user");
  let appUserEmail = "";
  try {
    const userObj = userStr ? JSON.parse(userStr) : null;
    appUserEmail = userObj?.email || "";
  } catch (e) {
    console.error("Failed to parse user from localStorage", e);
  }

  const isAuthorized = appUserEmail === 'contact@cloudai.in' || appUserEmail === 'sonuojha83@gmail.com';

  if (!isAuthorized) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
        <h2 style={{ color: '#f44336' }}>Access Denied</h2>
        <p style={{ color: '#555' }}>You do not have permission to view the admin page.</p>
        <button 
          onClick={() => window.history.back()} 
          style={{ 
            padding: '10px 20px', 
            cursor: 'pointer', 
            marginTop: '20px',
            backgroundColor: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontWeight: '600'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '1180px', padding: '8px', fontFamily: 'Inter, system-ui, sans-serif', boxSizing: 'border-box' }}>
      {!isLoggedIn ? (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <input
            type="email"
            placeholder="Enter your Gmail address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ padding: '10px', marginBottom: '10px', width: '300px' }}
          />
          <button onClick={handleGoogleLogin} style={{ padding: '15px 30px', fontSize: '18px', cursor: 'pointer' }}>
            Login with Gmail
          </button>
          {loginError && <p style={{ color: 'red', marginTop: '10px' }}>{loginError}</p>}
        </div>
      ) : selectedJob ? (
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: 'clamp(20px, 4vw, 36px)', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.07)' }}>
          <button onClick={handleBackToList} style={{ marginBottom: '24px', padding: '9px 14px', cursor: 'pointer', border: '1px solid #cbd5e1', borderRadius: '8px', color: '#334155', backgroundColor: '#ffffff', fontWeight: '600' }}>
            &larr; Back to List
          </button>
          <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '22px', marginBottom: '24px' }}>
            <p style={{ margin: '0 0 8px', color: '#2563eb', fontSize: '0.8rem', fontWeight: '800', letterSpacing: '0.08em' }}>TICKET #{selectedJob.jobRunId.substring(4, 10)}</p>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: 'clamp(1.35rem, 3vw, 1.8rem)' }}>{selectedJob.jobTitle || selectedJob.jobName}</h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 18px', marginTop: '14px', color: '#64748b', fontSize: '0.875rem' }}>
              <span><strong style={{ color: '#334155' }}>Status:</strong> {selectedJob.status}</span>
              <span><strong style={{ color: '#334155' }}>Created by:</strong> {selectedJob.createdBy}</span>
              <span><strong style={{ color: '#334155' }}>Created:</strong> {formatDateTime(selectedJob.startTime)}</span>
            </div>
          </div>
          <p style={{ color: '#475569', lineHeight: 1.65, margin: '0 0 28px' }}>{selectedJob.jobDescription}</p>
          <div>
            <h3 style={{ color: '#0f172a', margin: '0 0 16px', fontSize: '1.1rem' }}>Tasks</h3>
            {selectedJob.tasks.map((task, index) => (
              <div key={index} style={{ padding: '20px', marginBottom: '14px', border: '1px solid #e2e8f0', borderRadius: '12px', backgroundColor: '#f8fafc' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                  <strong style={{ color: '#0f172a' }}>{task.taskName}</strong>
                  <span style={{ padding: '4px 9px', borderRadius: '999px', backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: '700' }}>{task.status}</span>
                </div>
                <p style={{ margin: '10px 0 18px', color: '#475569', lineHeight: 1.55 }}>{task.taskDescription}</p>
                <div style={{ fontSize: '0.9em', borderTop: '1px solid #e2e8f0', paddingTop: '14px' }}>
                  <strong style={{ color: '#334155' }}>Comments</strong>
                  {task.comments && task.comments.length > 0 ? (
                    task.comments.map((comment, commentIdx) => (
                      <div key={comment.id || commentIdx} style={{ marginTop: '9px', color: '#475569', lineHeight: 1.5 }}>
                        {'• '}{renderCommentText(comment.text)}
                        {comment.userId && <em style={{ color: '#64748b' }}>{` (by ${comment.userId} at ${formatDateTime(comment.timestamp)})`}</em>}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontStyle: 'italic', color: '#64748b', marginTop: '8px' }}>No comments yet.</div>
                  )}
                </div>

                <div style={{ marginTop: '18px' }}>
                  <form onSubmit={(e) => handleAddComment(e, task.taskName)} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Add a comment as Admin..."
                      value={newComments[task.taskName] || ''}
                      onChange={(e) => handleCommentChange(task.taskName, e.target.value)}
                      style={{ flex: 1, minWidth: 0, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: '7px' }}
                    />
                    <button type="submit" style={{ padding: '10px 13px', border: 'none', backgroundColor: '#2563eb', color: 'white', borderRadius: '7px', cursor: 'pointer', fontWeight: '700', whiteSpace: 'nowrap' }}>
                      Add comment
                    </button>
                  </form>
                  {submitError && <div style={{ color: 'red', marginTop: '8px' }}>{submitError}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ overflow: 'hidden', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', boxShadow: '0 10px 30px rgba(15, 23, 42, 0.07)' }}>
          <div style={{ padding: 'clamp(22px, 4vw, 34px)', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #eff6ff, #ffffff)' }}>
            <p style={{ margin: '0 0 6px', color: '#2563eb', fontSize: '0.8rem', fontWeight: '800', letterSpacing: '0.08em' }}>ADMIN CENTER</p>
            <h1 style={{ margin: 0, color: '#0f172a', fontSize: 'clamp(1.45rem, 3vw, 2rem)' }}>All Tickets</h1>
            <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '0.9rem' }}>Review, manage, and respond to submitted tickets.</p>
          </div>
          {isLoading && <p style={{ padding: '24px', margin: 0, color: '#475569' }}>Loading tickets...</p>}
          {error && <p style={{ padding: '24px', margin: 0, color: '#b91c1c' }}>{error}</p>}
          {jobs.length > 0 && (
            <div style={{ overflowX: 'auto' }}>
              <table style={compact.table}>
                <thead>
                  <tr>
                    <th style={compact.th}>Ticket ID</th>
                    <th style={compact.th}>Title</th>
                    <th style={compact.th}>Status</th>
                    <th style={compact.th}>Created By</th>
                    <th style={compact.th}>Created At</th>
                    <th style={compact.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job, index) => (
                    <tr key={job.jobRunId} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                      <td style={compact.td}>
                        <button type="button" onClick={() => handleJobClick(job.jobRunId)} style={{ ...compact.idLink, padding: 0, border: 'none', background: 'transparent' }}>
                          #{job.jobRunId.substring(4, 10)}
                        </button>
                      </td>
                      <td style={{ ...compact.td, color: '#0f172a', fontWeight: '600' }}>{job.jobTitle || job.jobName}</td>
                      <td style={compact.td}><span style={{ display: 'inline-block', padding: '4px 9px', borderRadius: '999px', backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: '700' }}>{job.status}</span></td>
                      <td style={compact.td}>{job.createdBy}</td>
                      <td style={compact.td}>{formatDateTime(job.startTime)}</td>
                      <td style={compact.td}>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(job.jobRunId); }} style={compact.deleteBtn}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TrustAdminLogin;